// src/components/UploadDocumentsPopup.js (CONFIRMED - FINAL VERSION)

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    FaTimes, FaFileUpload, FaSpinner, FaExclamationTriangle,
    FaPaperclip, FaCheckCircle, FaFileAlt, FaTimesCircle,
    FaCamera, FaImages, FaInfoCircle,
    FaCheck, FaUndo // Keep icons for confirm/reject actions
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient';
import imageCompression from 'browser-image-compression';

// --- Helper Function to Format Keys (Unchanged) ---
const formatDataKey = (key) => {
    if (!key || typeof key !== 'string') return '';
    return key
        .replace(/_/g, ' ')
        .replace(/(?:^|\s)\S/g, a => a.toUpperCase());
};

// --- Formatted Data Display Component (Unchanged) ---
const FormattedDataDisplay = ({ data, styles }) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        return <div style={styles.noDataMessage}><FaInfoCircle style={{ marginRight: '5px' }} /> No data extracted or provided.</div>;
    }
    return (
        <dl style={styles.formattedDataList}>
            {Object.entries(data).map(([key, value]) => (
                <div key={key} style={styles.formattedDataItem}>
                    <dt style={styles.formattedDataKey}>{formatDataKey(key)}:</dt>
                    <dd style={styles.formattedDataValue}>
                        {value !== null && value !== undefined && value !== '' ? String(value) : <span style={styles.notAvailable}>N/A</span>}
                    </dd>
                </div>
            ))}
        </dl>
    );
};
FormattedDataDisplay.propTypes = {
    data: PropTypes.object,
    styles: PropTypes.object.isRequired,
};

// --- Individual Slot Status Component (Unchanged) ---
const DocumentValidationStatus = ({ status, message, styles }) => {
    let icon = null;
    let text = message || '';
    let color = '#6c757d';
    let spin = false;
    let fontWeight = 'normal';

    switch (status) {
        case 'idle': return null;
        case 'analyzing':
            icon = <FaSpinner />; spin = true; text = message || 'Analyzing...'; color = '#007bff'; break;
        case 'valid-no-confirm':
            icon = <FaCheckCircle />; text = 'Ready'; color = '#28a745'; break;
        case 'pending-confirmation':
            icon = <FaExclamationTriangle />; text = 'Review Needed Below'; color = '#ffc107'; fontWeight = 'bold'; break; // Updated text
        case 'user-confirmed':
            icon = <FaCheckCircle />; text = 'Details Confirmed'; color = '#28a745'; fontWeight = 'bold'; break;
        case 'analysis-error':
            icon = <FaTimesCircle />; text = `Error: ${message || 'Analysis failed'}`; color = '#dc3545'; break;
        case 'invalid-reason':
             icon = <FaTimesCircle />; text = `Invalid: ${message || 'Check failed'}`; color = '#dc3545'; break;
        case 'confirmed-yes':
            icon = <FaCheckCircle />; text = message || 'Verified'; color = '#28a745'; break;
        case 'validation-error': // Keep this for potential future use
            icon = <FaExclamationTriangle />; text = `Error: ${message || 'Validation failed'}`; color = '#dc3545'; break;
        default: console.warn(`Unknown validation status: ${status}`); return null;
    }

    return (
        <span style={{ ...styles.validationStatus, color: color, fontWeight: fontWeight }}>
            {React.cloneElement(icon, { className: spin ? 'fa-spin' : '', style: { marginRight: '6px' } })}
            {text}
        </span>
    );
};
DocumentValidationStatus.propTypes = {
    status: PropTypes.oneOf([
        'idle', 'analyzing', 'valid-no-confirm', 'pending-confirmation', 'user-confirmed',
        'analysis-error', 'invalid-reason', 'confirmed-yes', 'validation-error'
    ]),
    message: PropTypes.string,
    styles: PropTypes.object.isRequired,
};


// --- MODIFIED: Component for a single upload slot (Correction UI Updated) ---
const DocumentUploadSlot = ({
    slotData,
    displayFile,
    validationState,
    validationMessage,
    extractedData, // For review section
    isSubmitting,
    isAnalyzing,
    onFileSelect,
    onConfirmDetails,
    onRejectOrReupload,
    // --- Props for Correction UI ---
    showCorrectionOption,
    extractedNameForCorrection,
    onCorrectApplicantName, // This function INITIATES the process
    isCorrectingName, // Is correction API call in progress for THIS slot?
    styles
}) => {
    const { docName, docTypeId, status: backendStatus, details: rejectionReason } = slotData;
    const inputId = `file-upload-${docTypeId || docName.replace(/\s+/g, '-')}`;
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const galleryInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const uploadButtonDisabled = isSubmitting || isAnalyzing || isCorrectingName;
    const canUpload = ['idle', 'analysis-error', 'invalid-reason'].includes(validationState) || backendStatus === 'Rejected';
    const showReviewSection = validationState === 'pending-confirmation';

    const handleSelectFileClick = () => {
        if (/Mobi|Android/i.test(navigator.userAgent)) setShowChoiceModal(true);
        else galleryInputRef.current?.click();
    };
    const handleCameraSelect = () => { cameraInputRef.current?.click(); setShowChoiceModal(false); };
    const handleGallerySelect = () => { galleryInputRef.current?.click(); setShowChoiceModal(false); };
    const handleInputChange = (event) => {
        const file = event.target.files?.[0];
        if (file && docTypeId != null) onFileSelect(docTypeId, file);
        else if (!file) console.log(`[Doc Slot ${docTypeId || 'N/A'}] No file selected.`);
        else toast.error("Cannot select file: Document type info missing.");
        if (event.target) event.target.value = '';
        setShowChoiceModal(false);
    };

    const choiceModalStyles = {
         overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1070 },
         modal: { background: 'white', padding: '25px', borderRadius: '8px', textAlign: 'center', maxWidth: '300px', boxShadow: '0 3px 10px rgba(0,0,0,0.2)'},
         button: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: '1px solid #ccc', background: '#f8f9fa', cursor: 'pointer', fontSize: '1em' },
         icon: { marginRight: '10px'}
     };

    return (
        <div style={styles.docSlotItem} className={`slot-status-${validationState} ${showReviewSection ? 'review-visible' : ''} ${showCorrectionOption ? 'correction-visible' : ''}`}>
            <label htmlFor={canUpload && !uploadButtonDisabled ? inputId : undefined} style={{ ...styles.docSlotLabel, cursor: (canUpload && !uploadButtonDisabled) ? 'pointer' : 'default' }}>
                {docName}
                {rejectionReason && <span style={styles.backendStatusDetails}> (Rejected: {rejectionReason})</span>}
            </label>

            <div style={styles.docSlotInputArea}>
                {canUpload && (
                    <button
                        type="button"
                        onClick={handleSelectFileClick}
                        style={{ ...styles.docSlotButton, opacity: uploadButtonDisabled ? 0.6 : 1 }}
                        disabled={uploadButtonDisabled || docTypeId == null}
                        aria-label={`Select file for ${docName}`}
                    >
                        {(isAnalyzing || isCorrectingName) ? <FaSpinner className="fa-spin" size={13} style={{ marginRight: '6px' }} /> : <FaFileUpload size={13} style={{ marginRight: '6px' }} />}
                        {/* Updated loading text */}
                        {isAnalyzing ? 'Analyzing...' : (isCorrectingName ? 'Resolving...' : 'Select File')}
                    </button>
                )}
                <input type="file" ref={galleryInputRef} id={inputId} style={{ display: 'none' }} onChange={handleInputChange} disabled={uploadButtonDisabled} accept=".pdf,.png,.jpg,.jpeg,.webp" />
                <input type="file" ref={cameraInputRef} style={{ display: 'none' }} onChange={handleInputChange} disabled={uploadButtonDisabled} accept="image/*" capture="environment" />

                {displayFile && !isAnalyzing && !isCorrectingName && (
                    <span style={styles.docSlotFileName} title={displayFile.name}>
                        <FaPaperclip size={11} style={{ marginRight: '5px' }} />
                        {displayFile.name}
                    </span>
                )}

                {/* Updated status text during correction */}
                {isCorrectingName ? (
                    <span style={{ ...styles.validationStatus, color: '#007bff' }}>
                        <FaSpinner className="fa-spin" style={{ marginRight: '6px' }} />
                        Resolving Mismatch...
                    </span>
                ) : (
                    <DocumentValidationStatus
                        status={validationState}
                        message={validationMessage}
                        styles={styles}
                    />
                )}
            </div>

            {/* --- Correction Option Section --- */}
            {/* Shows if mismatch detected and name extracted */}
            {showCorrectionOption && !isCorrectingName && (
                <div style={styles.correctionSection}>
                    <FaInfoCircle style={{ marginRight: '8px', color: '#17a2b8', flexShrink: 0 }} />
                    <span style={{ flexGrow: 1 }}>
                        Name on ID seems to be: <strong>"{extractedNameForCorrection}"</strong>.
                    </span>
                    {/* Button always calls the same initiator function */}
                    <button
                        type="button"
                        onClick={() => onCorrectApplicantName(docTypeId, extractedNameForCorrection)}
                        style={{ ...styles.button, ...styles.correctNameButton }}
                        disabled={isSubmitting}
                        title={`Resolve name mismatch using "${extractedNameForCorrection}"?`} // Updated title
                    >
                        <FaCheck style={{ marginRight: '6px' }} /> Use This Name?
                    </button>
                </div>
            )}
            {/* --- End Correction Option Section --- */}


            {/* --- In-Place Review Section (Unchanged) --- */}
            {showReviewSection && (
                 <div style={styles.reviewSection}>
                    <h5 style={styles.reviewHeader}>Confirm Extracted Details:</h5>
                    <div style={styles.extractedDataContainerInline}>
                         <FormattedDataDisplay data={extractedData} styles={styles} />
                    </div>
                    <div style={styles.confirmationActionsInline}>
                        <p style={styles.confirmationQuestionInline}>Are these details correct?</p>
                        <div style={styles.confirmationButtonsInline}>
                            <button
                                type="button"
                                style={{ ...styles.button, ...styles.confirmSmallButton }}
                                onClick={() => onConfirmDetails(docTypeId)}
                                disabled={isSubmitting}
                                title="Confirm these details are correct"
                             >
                                <FaCheck style={{ marginRight: '6px' }}/> Yes, Confirm
                            </button>
                            <button
                                type="button"
                                style={{ ...styles.button, ...styles.rejectSmallButton }}
                                onClick={() => onRejectOrReupload(docTypeId, docName)}
                                disabled={isSubmitting}
                                title="Details are incorrect, I need to re-upload"
                             >
                                <FaUndo style={{ marginRight: '6px' }}/> Incorrect / Re-upload
                             </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Choice Modal (Unchanged) */}
            {showChoiceModal && (
                 <div style={choiceModalStyles.overlay} onClick={() => setShowChoiceModal(false)}>
                    <div style={choiceModalStyles.modal} onClick={(e) => e.stopPropagation()}>
                        <h4>Choose Upload Source</h4>
                        <button style={choiceModalStyles.button} onClick={handleCameraSelect}>
                            <FaCamera style={choiceModalStyles.icon} /> Camera
                        </button>
                        <button style={choiceModalStyles.button} onClick={handleGallerySelect}>
                            <FaImages style={choiceModalStyles.icon} /> Gallery / Files
                        </button>
                    </div>
                 </div>
             )}
        </div>
    );
};
// --- PropTypes for DocumentUploadSlot ---
DocumentUploadSlot.propTypes = {
    slotData: PropTypes.shape({
        docName: PropTypes.string.isRequired,
        docTypeId: PropTypes.number,
        status: PropTypes.string,
        details: PropTypes.string,
        extractedNameField: PropTypes.string,
    }).isRequired,
    displayFile: PropTypes.instanceOf(File),
    validationState: PropTypes.string.isRequired,
    validationMessage: PropTypes.string,
    extractedData: PropTypes.object,
    isSubmitting: PropTypes.bool.isRequired,
    isAnalyzing: PropTypes.bool.isRequired,
    onFileSelect: PropTypes.func.isRequired,
    onConfirmDetails: PropTypes.func.isRequired,
    onRejectOrReupload: PropTypes.func.isRequired,
    showCorrectionOption: PropTypes.bool.isRequired,
    extractedNameForCorrection: PropTypes.string,
    onCorrectApplicantName: PropTypes.func.isRequired,
    isCorrectingName: PropTypes.bool.isRequired,
    styles: PropTypes.object.isRequired,
};


// --- Main Popup Component ---
const UploadDocumentsPopup = ({ isOpen, onClose, orderItemId }) => {
    // --- State Variables ---
    const [uiData, setUiData] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [displayFiles, setDisplayFiles] = useState({});
    const [validationFiles, setValidationFiles] = useState({});
    const [validationStatus, setValidationStatus] = useState({});
    const [filesPendingAnalysis, setFilesPendingAnalysis] = useState({});
    const [analysisData, setAnalysisData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [isCorrectingNameForDocId, setIsCorrectingNameForDocId] = useState(null);
    const [showCorrectionConfirmModal, setShowCorrectionConfirmModal] = useState(false);
    // Simplified state for correction action
    const [correctionDetails, setCorrectionDetails] = useState({ docTypeId: null, newName: null });
    // --- End State ---

    // --- Memos ---
    const isAnyAnalysisRunning = useMemo(() => Object.keys(filesPendingAnalysis).length > 0, [filesPendingAnalysis]);
    const canSubmitFinally = useMemo(() => {
        if (isSubmitting || isLoadingData || isAnyAnalysisRunning || !uiData || isCorrectingNameForDocId !== null) return false;

        let allMandatorySatisfied = true;
        let filesReadyInSessionCount = 0;
        const readyStatesInSession = ['valid-no-confirm', 'user-confirmed'];

        uiData.sections?.forEach(section => {
            const isMandatorySection = section.type !== 'optional';
            section.slots.forEach(slot => {
                if (!slot.docTypeId || !isMandatorySection) return;

                const backendStatus = slot.status;
                const currentValidation = validationStatus[slot.docTypeId];
                const sessionStatus = currentValidation?.status;
                const isReadyInSession = readyStatesInSession.includes(sessionStatus);

                // If not already verified and not ready in this session, not satisfied
                if (backendStatus !== 'Verified' && !isReadyInSession) {
                    // Allow 'invalid-reason' if a file exists (user needs to fix it)
                    if (!(sessionStatus === 'invalid-reason' && validationFiles[slot.docTypeId])) {
                        allMandatorySatisfied = false;
                    }
                }

                // Count files processed in this session
                if (isReadyInSession && validationFiles[slot.docTypeId]) {
                    filesReadyInSessionCount++;
                }
            });
        });

        // Check if all slots are already verified or optional (no files needed)
        const noFilesInSessionNeeded = filesReadyInSessionCount === 0 &&
            uiData.sections?.flatMap(section =>
                section.slots.map(slot =>
                    !slot.docTypeId || slot.status === 'Verified' || section.type === 'optional'
                )
            ).every(Boolean);

        // Allow submit if all mandatory are satisfied AND (files processed OR no files needed)
        return allMandatorySatisfied && (filesReadyInSessionCount > 0 || noFilesInSessionNeeded);

    }, [isSubmitting, isLoadingData, isAnyAnalysisRunning, validationStatus, uiData, validationFiles, isCorrectingNameForDocId]);

    // --- Effect to Fetch UI Data & Reset State ---
    useEffect(() => {
        const resetState = () => {
            setUiData(null); setIsLoadingData(false); setFetchError(null);
            setDisplayFiles({}); setValidationFiles({}); setValidationStatus({});
            setFilesPendingAnalysis({}); setAnalysisData({});
            setIsSubmitting(false); setSubmissionStatus(null);
            setIsCorrectingNameForDocId(null);
            setShowCorrectionConfirmModal(false);
            setCorrectionDetails({ docTypeId: null, newName: null }); // Reset simplified state
        };

        if (!isOpen || !orderItemId) { resetState(); return; }

        const fetchUiData = async () => {
            resetState();
            console.log(`[Doc Popup] Fetching UI data for Item ID: ${orderItemId}`);
            setIsLoadingData(true);
            try {
                const response = await apiClient.get(`/order-items/${orderItemId}/required-documents-ui`);
                console.log("[Doc Popup] UI API Response:", response.data);

                if (response.data && Array.isArray(response.data.requirements)) {
                    const rawData = response.data;
                    const transformedData = {
                        orderId: rawData.orderId,
                        serviceName: rawData.serviceName,
                        applicantName: rawData.applicantName,
                        applicantId: rawData.applicantId,
                        sections: [{
                            title: "Required Documents",
                            type: rawData.sectionType || "mandatory",
                            slots: rawData.requirements.map(req => ({
                                docName: req.label,
                                docTypeId: req.docTypeId,
                                status: req.status,
                                details: req.rejectionReason || null,
                                extractedNameField: req.extractedNameField || 'full_name',
                            }))
                        }]
                    };
                    if (!transformedData.applicantId) {
                         console.warn("WARN: applicantId missing from /required-documents-ui response!");
                    }
                    setUiData(transformedData);

                    const initialStatus = {};
                    transformedData.sections?.forEach(sec => {
                        sec.slots.forEach(slot => {
                            if (!slot.docTypeId) return;
                            if (slot.status === 'Verified') initialStatus[slot.docTypeId] = { status: 'confirmed-yes', message: 'Verified' };
                            else if (slot.status === 'Rejected') initialStatus[slot.docTypeId] = { status: 'invalid-reason', message: slot.details || 'Rejected' };
                            else initialStatus[slot.docTypeId] = { status: 'idle' };
                        });
                    });
                    setValidationStatus(initialStatus);

                } else { throw new Error("Invalid data structure received from UI endpoint"); }
            } catch (err) {
                 console.error("[Doc Popup] Error fetching UI data:", err);
                 setFetchError(err.response?.data?.error || err.message || "Could not fetch requirements.");
            } finally {
                 setIsLoadingData(false);
            }
        };
        fetchUiData();
    }, [isOpen, orderItemId]);


    // --- File Analysis Handler ---
    const analyzeFile = useCallback(async (docTypeId, file) => {
        const originalFile = file;
        const slotConfig = uiData?.sections?.flatMap(s => s.slots).find(s => s.docTypeId === docTypeId);

        if (!originalFile || !docTypeId || !orderItemId || filesPendingAnalysis[docTypeId] || !slotConfig) {
            if (!slotConfig) console.error(`Cannot find slot config for docTypeId ${docTypeId}`);
            else console.log(`Skipping analysis for ${docTypeId}: missing data or already analyzing.`);
            return;
        }
        const extractedNameFieldKey = slotConfig.extractedNameField || 'full_name';

        console.log(`[AnalyzeFile ${docTypeId}] Starting analysis for: ${originalFile.name}`);
        setDisplayFiles(prev => ({ ...prev, [docTypeId]: originalFile }));
        setFilesPendingAnalysis(prev => ({ ...prev, [docTypeId]: true }));
        setAnalysisData(prev => ({
            ...prev,
            [docTypeId]: {
                analysis: null, extractedData: null, extractedNameForCorrection: null,
                applicantId: prev[docTypeId]?.applicantId
            }
        }));
        setValidationFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
        setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'analyzing', message: 'Preparing...' } }));

        let fileToAnalyze = originalFile;
        let fileForSubmission = originalFile;

        if (['image/jpeg', 'image/png', 'image/webp'].includes(originalFile.type)) {
            const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1920, useWebWorker: true, initialQuality: 0.75 };
            try {
                setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'analyzing', message: 'Compressing...' } }));
                const compressedFileBlob = await imageCompression(originalFile, options);
                fileToAnalyze = new File([compressedFileBlob], `compressed_${originalFile.name}`, { type: compressedFileBlob.type });
                fileForSubmission = fileToAnalyze;
                console.log(`Compression done: ${(fileToAnalyze.size / 1024).toFixed(1)} KB`);
            } catch (err) {
                console.error(`Compression failed:`, err);
                setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'analysis-error', message: 'Compression failed.' } }));
                setDisplayFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
                setFilesPendingAnalysis(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
                return;
            }
        }

        setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'analyzing', message: 'Analyzing...' } }));
        const formData = new FormData();
        formData.append('document', fileToAnalyze);
        formData.append('intendedDocTypeId', String(docTypeId));
        formData.append('orderItemId', String(orderItemId));

        console.log(`[AnalyzeFile ${docTypeId}] Calling POST /api/documents/analyze-upload...`);
        try {
            const response = await apiClient.post(`/documents/analyze-upload`, formData);
            const data = response.data;
            console.log(`[AnalyzeFile ${docTypeId}] Response:`, data);

             setAnalysisData(prev => ({
                 ...prev,
                 [docTypeId]: {
                    ...prev[docTypeId],
                    analysis: data.analysis,
                    extractedData: data.extractedData,
                    applicantId: data.applicantId // Store ID from response (could be original, matched, or null)
                 }
            }));

            if (data?.isValid) {
                 setValidationFiles(prev => ({ ...prev, [docTypeId]: fileForSubmission }));
                 setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: data.needsConfirmation ? 'pending-confirmation' : 'valid-no-confirm', message: data.needsConfirmation ? 'Review Needed Below' : 'Ready' } }));
            } else {
                 console.warn(`[AnalyzeFile ${docTypeId}] Validation failed: ${data?.reason} (Code: ${data?.reasonCode || 'N/A'})`);
                 const statusUpdate = {
                     status: ['NAME_MISMATCH', 'WRONG_TYPE', 'POOR_QUALITY'].includes(data?.reasonCode) ? 'invalid-reason' : 'analysis-error',
                     message: data?.reason || 'Validation failed.'
                 };
                 setValidationStatus(prev => ({ ...prev, [docTypeId]: statusUpdate }));

                 if (data?.reasonCode === 'NAME_MISMATCH') {
                     setValidationFiles(prev => ({ ...prev, [docTypeId]: fileForSubmission }));
                     const extractedName = data.extractedData?.[extractedNameFieldKey];
                     setAnalysisData(prev => ({
                         ...prev,
                         [docTypeId]: {
                             ...prev[docTypeId], // Keep analysis, extractedData, applicantId already set
                             extractedNameForCorrection: extractedName
                         }
                     }));
                     console.log(`[AnalyzeFile ${docTypeId}] Name mismatch state updated. Extracted Name: "${extractedName}", ApplicantID in state: ${data.applicantId}`);
                 } else {
                     setValidationFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
                 }
            }
        } catch (err) {
             console.error(`[AnalyzeFile ${docTypeId}] API error:`, err);
             const errorMsg = err.response?.data?.error || err.response?.data?.reason || err.message || "Request failed.";
             setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'analysis-error', message: `Error: ${errorMsg}` } }));
             setDisplayFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
             setValidationFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
             setAnalysisData(prev => ({ ...prev, [docTypeId]: undefined })); // Clear slot data on error
        } finally {
            setFilesPendingAnalysis(prev => { const newState = { ...prev }; delete newState[docTypeId]; return newState; });
            console.log(`[AnalyzeFile ${docTypeId}] Finished.`);
        }
    }, [orderItemId, filesPendingAnalysis, uiData, setUiData]); // Include uiData & setUiData dependencies


    // --- Confirmation Handlers (Unchanged) ---
    const handleConfirmDetails = useCallback((docTypeId) => {
        console.log(`[In-Place Confirm] Confirmed details for docTypeId: ${docTypeId}`);
        setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'user-confirmed', message: 'Details Confirmed' } }));
        toast.info(`Details confirmed.`);
    }, []);

    const handleRejectOrReupload = useCallback((docTypeId, docName) => {
        console.log(`[In-Place Reject] Rejected details for docTypeId: ${docTypeId}`);
        setDisplayFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
        setValidationFiles(prev => { const ns = { ...prev }; delete ns[docTypeId]; return ns; });
        setAnalysisData(prev => ({ ...prev, [docTypeId]: undefined })); // Clear slot data
        setValidationStatus(prev => ({ ...prev, [docTypeId]: { status: 'idle' } }));
        toast.warn(`Details rejected for ${docName}. Please re-upload the correct document.`);
    }, []);


    // --- MODIFIED Handler: Initiate Name Correction / Resolution ---
    const handleCorrectApplicantName = useCallback((docTypeId, extractedName) => {
        if (!extractedName || docTypeId === null) {
             toast.error("Cannot initiate name resolution: Missing extracted name or document type ID.");
             console.error("Resolution data missing:", { extractedName, docTypeId });
             return;
        }
        console.log(`Initiating name resolution for DocTypeID: ${docTypeId}. Extracted name: "${extractedName}"`);
        setCorrectionDetails({ docTypeId, newName: extractedName });
        setShowCorrectionConfirmModal(true);
    }, []);


    // --- UPDATED Handler: Execute Name Correction/Resolution ---
    const executeNameCorrection = useCallback(async () => {
        const { docTypeId, newName: extractedName } = correctionDetails;
        const currentOrderItemId = orderItemId;

        if (!extractedName || docTypeId === null || !currentOrderItemId) {
            toast.error("Resolution failed: Missing critical information.");
            console.error("Resolve data missing:", { extractedName, docTypeId, currentOrderItemId });
            setShowCorrectionConfirmModal(false); // Only close confirmation modal
            return;
        }

        try {
            // First close the confirmation modal only
            setShowCorrectionConfirmModal(false);
            setIsCorrectingNameForDocId(docTypeId);
            
            console.log(`Calling POST /api/order-items/${currentOrderItemId}/resolve-name-mismatch with name: "${extractedName}"`);
            const response = await apiClient.post(`/order-items/${currentOrderItemId}/resolve-name-mismatch`, { extractedName });

            if (response.status === 200 && response.data?.success) {
                const successMsg = response.data.message || `Item assigned to "${extractedName}". Re-validating document...`;
                const assignedApplicantId = response.data.applicantId;
                toast.success(successMsg);

                // Refetch UI data and re-analyze
                setIsLoadingData(true);
                try {
                    const uiResponse = await apiClient.get(`/order-items/${currentOrderItemId}/required-documents-ui`);
                    // ... rest of the refetch and re-analysis logic ...

                } catch (err) {
                    console.error("Error refetching UI data after name correction:", err);
                    toast.error("Failed to refresh document requirements after name correction.");
                    throw err;
                } finally {
                    setIsLoadingData(false);
                }
            } else {
                throw new Error(response.data?.error || response.data?.message || "Failed to resolve name mismatch.");
            }
        } catch (err) {
            console.error("Error resolving name mismatch:", err);
            const errorDetail = err.response?.data?.error || err.message || "An unknown error occurred.";
            toast.error(`Operation failed: ${errorDetail}`);
        } finally {
            // Only reset the correction-related states
            setIsCorrectingNameForDocId(null);
            setCorrectionDetails({ docTypeId: null, newName: null });
            // Do NOT call onClose here - let the main popup stay open
        }
    }, [correctionDetails, orderItemId, validationFiles, analyzeFile, setUiData]);


    // --- Final Submission Handler (MODIFIED FOR CONFIRMATION PATH) ---
    const handleSubmit = useCallback(async () => {
        if (isCorrectingNameForDocId !== null) {
            toast.warn("Please wait for name resolution to complete.");
            return;
        }
        console.log("[Submit] Initiating final submission.");

        // --- Check if submission is just a confirmation of pre-verified status ---
        const readyStatesInSession = ['valid-no-confirm', 'user-confirmed'];
        let filesReadyInSessionCount = 0;
        let allRequiredDocsVerified = true;

        // First pass: Count files ready in this session
        validationStatus && Object.entries(validationStatus).forEach(([docTypeIdStr, statusObj]) => {
            const docTypeId = parseInt(docTypeIdStr, 10);
            if (readyStatesInSession.includes(statusObj?.status) && validationFiles[docTypeId]) {
                filesReadyInSessionCount++;
            }
        });

        // Second pass: Check if everything is already verified
        uiData?.sections?.forEach(section => {
            const isMandatorySection = section.type !== 'optional';
            section.slots.forEach(slot => {
                if (!slot.docTypeId || !isMandatorySection) return;
                if (slot.status !== 'Verified') {
                    allRequiredDocsVerified = false;
                }
            });
        });

        // If no new files AND all required docs are verified, just confirm and close
        if (filesReadyInSessionCount === 0 && allRequiredDocsVerified && canSubmitFinally) {
            console.log("[Submit] All requirements already satisfied.");

            try {
                // Call backend to mark as submitted
                if (orderItemId) {
                    await apiClient.post(`/order-items/${orderItemId}/submit-documents`);
                    toast.success("Documents submitted for review.");
                } else {
                    toast.success("All requirements confirmed.");
                }

                setSubmissionStatus({ type: 'success', message: 'Requirements confirmed and submitted.' });
                
                // Clear state before closing
                setDisplayFiles({});
                setValidationFiles({});
                setFilesPendingAnalysis({});
                setValidationStatus({});
                setAnalysisData({});
                
                // Close popup after delay
                setTimeout(() => { onClose(true); }, 1500);
                return;
            } catch (error) {
                console.error("[Submit] Error marking documents as submitted:", error);
                toast.error("Failed to submit documents for review.");
                return;
            }
        }

        // --- Proceed with file upload if we have new files ---
        const filesToSubmitDetails = [];

        Object.entries(validationStatus).forEach(([docTypeIdStr, statusObj]) => {
            const docTypeId = parseInt(docTypeIdStr, 10);
            if (readyStatesInSession.includes(statusObj?.status) && validationFiles[docTypeId]) {
                const file = validationFiles[docTypeId];
                const analysisResult = analysisData[docTypeId]?.analysis || analysisData[docTypeId]?.extractedData || {};
                if (file instanceof File) {
                    filesToSubmitDetails.push({ docTypeId, file, analysis: analysisResult });
                } else {
                    console.warn(`[Submit] Invalid file data for docTypeId ${docTypeId}`);
                    toast.error(`Internal error preparing document ${docTypeId}. Please retry.`);
                }
            }
        });

        const orderId = uiData?.orderId;
        if (!orderId) {
            toast.error("Missing Order ID.");
            return;
        }

        // Verify all mandatory documents are accounted for
        let missingMandatory = false;
        let missingDocNames = [];
        const submittedDocTypeIds = new Set(filesToSubmitDetails.map(f => f.docTypeId));

        uiData.sections?.forEach(section => {
            const isMandatorySection = section.type !== 'optional';
            section.slots.forEach(slot => {
                if (!slot.docTypeId || !isMandatorySection) return;
                
                const currentValidation = validationStatus[slot.docTypeId];
                const status = currentValidation?.status;
                const isVerified = slot.status === 'Verified';
                const isReadyInSession = readyStatesInSession.includes(status);
                const hasFileInSession = submittedDocTypeIds.has(slot.docTypeId);

                if (!isVerified && !isReadyInSession) {
                    // Skip if file exists and is just waiting for correction
                    if (!(status === 'invalid-reason' && validationFiles[slot.docTypeId])) {
                        missingMandatory = true;
                        missingDocNames.push(slot.docName);
                    }
                }
            });
        });

        if (missingMandatory) {
            const message = `Mandatory document(s) missing or not confirmed: ${missingDocNames.join(', ')}`;
            toast.error(message);
            setSubmissionStatus({ type: 'error', message });
            return;
        }

        // Prepare and submit files
        console.log(`[Submit] Preparing ${filesToSubmitDetails.length} files for order ${orderId}`);
        setIsSubmitting(true);
        setSubmissionStatus({ type: 'info', message: 'Submitting...' });

        const formData = new FormData();
        const docTypeIdsArray = [];
        const analysisResultsArray = [];

        filesToSubmitDetails.forEach(detail => {
            formData.append('documents', detail.file, detail.file.name);
            docTypeIdsArray.push(String(detail.docTypeId));
            analysisResultsArray.push(detail.analysis || {});
        });

        formData.append('docTypeIds', JSON.stringify(docTypeIdsArray));
        formData.append('analysisResults', JSON.stringify(analysisResultsArray));

        try {
            const response = await apiClient.post(`/orders/${orderId}/upload`, formData);
            const successMsg = response.data.message || `${filesToSubmitDetails.length} document(s) submitted successfully!`;
            toast.success(successMsg);
            setSubmissionStatus({ type: 'success', message: successMsg });
            
            // Reset state
            setDisplayFiles({});
            setValidationFiles({});
            setFilesPendingAnalysis({});
            setValidationStatus({});
            setAnalysisData({});
            
            setTimeout(() => { onClose(true); }, 2000);
        } catch (err) {
            console.error("[Submit] Upload failed:", err);
            const errorMsg = err.response?.data?.error || err.message || "Submission failed.";
            toast.error(`Error: ${errorMsg}`);
            setSubmissionStatus({ type: 'error', message: `Error: ${errorMsg}` });
        } finally {
            setIsSubmitting(false);
        }

    }, [
        validationStatus, validationFiles, analysisData, uiData, onClose,
        isCorrectingNameForDocId, orderItemId, canSubmitFinally, apiClient,
        setDisplayFiles, setValidationFiles, setFilesPendingAnalysis,
        setValidationStatus, setAnalysisData, setSubmissionStatus
    ]);


    // --- Render Logic ---
    if (!isOpen) return null;
    const handleModalClick = (e) => e.stopPropagation();

    return (
        <div style={styles.overlay} onClick={() => !isSubmitting && !isCorrectingNameForDocId && onClose(false)} role="dialog" aria-modal="true" aria-labelledby="upload-popup-title">
            <div style={styles.modalContent} onClick={handleModalClick}>
                 {/* Header (Unchanged) */}
                 <button style={styles.closeButton} onClick={() => !isSubmitting && !isCorrectingNameForDocId && onClose(false)} aria-label="Close Popup" disabled={isSubmitting || isCorrectingNameForDocId !== null}>
                    <FaTimes />
                 </button>
                 <h2 id="upload-popup-title" style={styles.title}>
                    <FaFileUpload style={{ marginRight: '12px', color: '#007bff' }} /> Document Collection
                 </h2>

                 {/* Loading / Error States (Unchanged) */}
                 {isLoadingData && <div style={styles.centeredMessage}><FaSpinner className="fa-spin fa-lg" style={{marginRight: '10px'}}/> Loading Requirements...</div>}
                 {fetchError && !isLoadingData && <div style={styles.errorDisplay}><FaExclamationTriangle style={{ marginRight: '8px' }}/> {fetchError}</div>}

                 {/* Main Content */}
                 {!isLoadingData && !fetchError && uiData && (
                    <>
                        {/* Info & Status (Unchanged) */}
                        <div style={styles.serviceInfo}>
                             <div>Service: <strong>{uiData.serviceName || 'N/A'}</strong></div>
                             <div>Applicant: <strong>{uiData.applicantName || 'N/A'}</strong> (ID: {uiData.applicantId || 'N/A'}) (Item ID: {orderItemId})</div>
                        </div>
                        {/* Submission Status Display (Unchanged) */}
                        {submissionStatus?.type === 'error' && <div style={{...styles.errorDisplay, marginBottom: '15px'}}><FaExclamationTriangle style={{ marginRight: '8px' }}/> {submissionStatus.message}</div>}
                        {submissionStatus?.type === 'success' && <div style={{...styles.successDisplay, marginBottom: '15px'}}><FaCheckCircle style={{ marginRight: '8px' }}/> {submissionStatus.message}</div>}
                        {submissionStatus?.type === 'info' && <div style={{...styles.infoDisplay, marginBottom: '15px'}}><FaSpinner className="fa-spin" style={{ marginRight: '8px' }}/> {submissionStatus.message}</div>}


                        {/* Document Slots */}
                        <div style={styles.tabContent}>
                            <div style={styles.docsContainer}>
                                {(uiData.sections || []).map((section, secIndex) => (
                                    <section key={`sec-${secIndex}-${section.title}`} style={styles.sectionContainer}>
                                        <h3 style={styles.sectionHeader}>{section.title || `Required Documents`}</h3>
                                        {(section.slots || []).map((slot) => {
                                            if (!slot.docTypeId) {
                                                console.warn("Skipping slot rendering, missing docTypeId:", slot);
                                                return null;
                                            }
                                            const docTypeId = slot.docTypeId;
                                            const currentAnalysisData = analysisData[docTypeId];
                                            const currentValidation = validationStatus[docTypeId];

                                            // --- Determine if Correction Option should be shown ---
                                            const isNameMismatchError = currentValidation?.status === 'invalid-reason' &&
                                                                        currentValidation?.message?.toLowerCase().includes('name mismatch');
                                            const extractedName = currentAnalysisData?.extractedNameForCorrection;
                                            const showCorrection = isNameMismatchError && !!extractedName;
                                            // --- End Correction Check ---

                                            return (
                                                <DocumentUploadSlot
                                                    key={docTypeId}
                                                    slotData={slot}
                                                    displayFile={displayFiles[docTypeId]}
                                                    validationState={currentValidation?.status || 'idle'}
                                                    validationMessage={currentValidation?.message}
                                                    extractedData={currentAnalysisData?.extractedData}
                                                    isSubmitting={isSubmitting}
                                                    isAnalyzing={!!filesPendingAnalysis[docTypeId]}
                                                    onFileSelect={analyzeFile}
                                                    onConfirmDetails={handleConfirmDetails}
                                                    onRejectOrReupload={handleRejectOrReupload}
                                                    showCorrectionOption={showCorrection}
                                                    extractedNameForCorrection={extractedName}
                                                    onCorrectApplicantName={handleCorrectApplicantName}
                                                    isCorrectingName={isCorrectingNameForDocId === docTypeId}
                                                    styles={styles}
                                                />
                                            );
                                        })}
                                        {(!section.slots || section.slots.length === 0) && <p style={styles.centeredMessage}>No documents required for this section.</p> }
                                    </section>
                                ))}
                                {(!uiData.sections || uiData.sections.length === 0) && <p style={styles.centeredMessage}>No requirements found.</p>}
                            </div>
                        </div>

                        {/* Footer Buttons (Unchanged) */}
                         <div style={styles.footer}>
                             <div style={styles.buttonContainer}>
                                 <button type="button" style={{ ...styles.button, ...styles.cancelButton }} onClick={() => onClose(false)} disabled={isSubmitting || isCorrectingNameForDocId !== null}>Cancel</button>
                                 <button
                                     type="button"
                                     style={{ ...styles.button, ...styles.confirmButton, opacity: (!canSubmitFinally || isCorrectingNameForDocId !== null) ? 0.6 : 1 }}
                                     onClick={handleSubmit}
                                     disabled={!canSubmitFinally || isCorrectingNameForDocId !== null}
                                     title={!canSubmitFinally ? "Please upload/confirm all mandatory documents" : (isCorrectingNameForDocId !== null ? "Resolving name mismatch..." : "Submit all confirmed documents")}
                                   >
                                      {(isSubmitting || isCorrectingNameForDocId !== null) ? <FaSpinner className="fa-spin" style={{marginRight: '8px'}}/> : <FaFileUpload style={{marginRight: '8px'}}/>}
                                      {isSubmitting ? 'Submitting...' : (isCorrectingNameForDocId !== null ? 'Processing...' : 'Submit All Documents')}
                                  </button>
                             </div>
                         </div>
                    </>
                 )}
            </div> {/* End modalContent */}

             {/* --- Confirmation Modal (Text Updated) --- */}
             {showCorrectionConfirmModal && (
                 <div style={styles.confirmationModalOverlay}>
                    <div style={styles.confirmationModalContent}>
                        <h4>Confirm Name Resolution</h4>
                        <p style={{ margin: '20px 0' }}>
                            Use the name found on the document: <br/>
                            <strong style={{ fontSize: '1.1em', display: 'block', marginTop: '5px' }}>"{correctionDetails.newName}"</strong>?
                        </p>
                        <p style={{fontSize: '0.9em', color: '#6c757d'}}>
                            This will assign the order item to this applicant. If the applicant doesn't exist, a new record will be created.
                        </p>
                        <div style={styles.confirmationModalActions}>
                            <button
                                type="button"
                                style={{ ...styles.button, ...styles.cancelButton }}
                                onClick={() => setShowCorrectionConfirmModal(false)}
                                disabled={isCorrectingNameForDocId !== null}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={{ ...styles.button, ...styles.confirmButton }}
                                onClick={executeNameCorrection} // Calls the updated function
                                disabled={isCorrectingNameForDocId !== null}
                             >
                                {isCorrectingNameForDocId === correctionDetails.docTypeId ? <FaSpinner className="fa-spin" style={{ marginRight: '8px' }} /> : <FaCheck style={{ marginRight: '8px' }} />}
                                Yes, Use This Name
                             </button>
                        </div>
                    </div>
                </div>
            )}
            {/* --- End Confirmation Modal --- */}

        </div> // End overlay
    );
};


// --- PropTypes ---
UploadDocumentsPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderItemId: PropTypes.number,
};

// --- Styles ---
const styles = {
    // --- Existing Styles (Keep all of them) ---
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '20px' },
    modalContent: { backgroundColor: 'white', padding: '30px 40px', borderRadius: '8px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', position: 'relative', width: '90%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
    closeButton: { position: 'absolute', top: '15px', right: '20px', background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#6c757d', padding: '5px', lineHeight: 1 },
    title: { marginTop: 0, marginBottom: '25px', borderBottom: '1px solid #eee', paddingBottom: '15px', color: '#333', fontSize: '1.6em', display: 'flex', alignItems: 'center' },
    serviceInfo: { marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6', fontSize: '0.95em', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' },
    tabContent: { flexGrow: 1, overflowY: 'auto', padding: '5px 10px 5px 5px', marginRight: '-10px', minHeight: '200px', maxHeight: 'calc(90vh - 320px)' },
    docsContainer: { paddingRight: '10px' },
    sectionContainer: { marginBottom: '25px' },
    sectionHeader: { fontSize: '1.2em', color: '#495057', borderBottom: '2px solid #007bff', paddingBottom: '8px', marginBottom: '20px' },
    docSlotItem: { marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #f1f1f1', transition: 'padding-bottom 0.3s ease, margin-bottom 0.3s ease', position: 'relative' },
    docSlotLabel: { display: 'block', fontWeight: '600', marginBottom: '8px', color: '#555' },
    backendStatusDetails: { fontWeight: 'normal', color: '#dc3545', fontSize: '0.9em', marginLeft: '8px' },
    docSlotInputArea: { display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginTop: '5px', marginBottom: '10px' },
    docSlotButton: { padding: '6px 12px', fontSize: '0.9em', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', '&:hover': { backgroundColor: '#0056b3' } },
    docSlotFileName: { fontSize: '0.9em', color: '#555', display: 'inline-flex', alignItems: 'center', backgroundColor: '#e9ecef', padding: '5px 10px', borderRadius: '4px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    validationStatus: { fontSize: '0.9em', marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', fontWeight: '500', padding: '3px 8px', borderRadius: '4px', textAlign: 'right', minWidth: '120px', justifyContent: 'flex-end' },
    reviewSection: { marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', animation: 'fadeIn 0.5s ease' },
    reviewHeader: { marginTop: 0, marginBottom: '10px', fontSize: '1.1em', fontWeight: 600, color: '#495057', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' },
    extractedDataContainerInline: { padding: '10px 0', backgroundColor: 'transparent' },
    confirmationActionsInline: { paddingTop: '15px', borderTop: '1px solid #e0e0e0', marginTop: '10px' },
    confirmationQuestionInline: { fontWeight: 'bold', marginBottom: '15px', fontSize: '1em', color: '#444', textAlign: 'center' },
    confirmationButtonsInline: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' },
    formattedDataList: { margin: 0, padding: 0 },
    formattedDataItem: { display: 'flex', marginBottom: '8px', fontSize: '0.95em', borderBottom: '1px dotted #eee', paddingBottom: '8px' },
    formattedDataKey: { fontWeight: '600', color: '#555', marginRight: '10px', minWidth: '120px', flexShrink: 0 },
    formattedDataValue: { margin: 0, color: '#333', wordBreak: 'break-word' },
    notAvailable: { color: '#888', fontStyle: 'italic' },
    noDataMessage: { color: '#6c757d', fontStyle: 'italic', padding: '10px 0', display: 'flex', alignItems: 'center' },
    footer: { borderTop: '1px solid #ddd', paddingTop: '20px', marginTop: '25px' },
    buttonContainer: { display: 'flex', justifyContent: 'flex-end', gap: '15px' },
    button: { padding: '10px 22px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: '500', fontSize: '1em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', lineHeight: 1.5 }, // Base button
    confirmButton: { backgroundColor: '#28a745', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#218838' } },
    cancelButton: { backgroundColor: '#6c757d', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#5a6268' } },
    confirmSmallButton: { backgroundColor: '#28a745', color: 'white', padding: '6px 12px', fontSize: '0.9em', '&:hover:not(:disabled)': { backgroundColor: '#218838' } },
    rejectSmallButton: { backgroundColor: '#dc3545', color: 'white', padding: '6px 12px', fontSize: '0.9em', '&:hover:not(:disabled)': { backgroundColor: '#c82333' } },
    centeredMessage: { textAlign: 'center', color: '#6c757d', padding: '20px' },
    errorDisplay: { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', marginBottom: '15px'},
    successDisplay: { backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', marginBottom: '15px'},
    infoDisplay: { backgroundColor: '#d1ecf1', color: '#0c5460', border: '1px solid #bee5eb', padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', marginBottom: '15px'},
    '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-10px)' }, '100%': { opacity: 1, transform: 'translateY(0)' }, },

    // --- Styles for Correction UI ---
    correctionSection: {
        marginTop: '10px', marginBottom: '10px', padding: '10px 15px',
        backgroundColor: '#e2f3f5', border: '1px solid #bdedf5', borderRadius: '4px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '15px', fontSize: '0.9em', flexWrap: 'wrap',
    },
    correctNameButton: {
        backgroundColor: '#17a2b8', color: 'white', padding: '4px 10px',
        fontSize: '0.85em', marginLeft: 'auto', flexShrink: 0, border: 'none',
        borderRadius: '4px', cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', '&:hover:not(:disabled)': { backgroundColor: '#138496' },
    },
    confirmationModalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1060,
    },
    confirmationModalContent: {
        backgroundColor: 'white', padding: '30px', borderRadius: '8px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)', maxWidth: '450px', textAlign: 'center',
    },
    confirmationModalActions: {
        marginTop: '25px', display: 'flex', justifyContent: 'center', gap: '15px',
    },
};

export default UploadDocumentsPopup;